import { environment } from '@symbo.ls/sdk'

const PLUGIN_SESSION_STORAGE_KEY = 'plugin_auth_session'

const ensurePluginSession = () => {
  if (typeof window === 'undefined') {
    return null
  }

  let session = null

  try {
    session = new URL(window.location.href).searchParams.get('session')
  } catch {
    // Ignore URL parsing errors
  }

  if (session) {
    try {
      if (window.localStorage) {
        window.localStorage.setItem(PLUGIN_SESSION_STORAGE_KEY, session)
      }
    } catch {
      // Ignore storage errors
    }
    return session
  }

  try {
    if (window.localStorage) {
      return window.localStorage.getItem(PLUGIN_SESSION_STORAGE_KEY)
    }
  } catch {
    // Ignore storage errors
  }

  return null
}

export async function handleGoogleAuthResponse(credential) {
  const state = this.getRootState()
  try {
    // Get invite token from localStorage instead of URL
    const inviteToken = localStorage.getItem('google_auth_invite_token')

    const pluginSession = ensurePluginSession()
    const loginResult = pluginSession
      ? await this.sdk.googleAuth(credential, inviteToken, {
          session: pluginSession
        })
      : await this.sdk.googleAuth(credential, inviteToken)

    if (!loginResult) {
      throw new Error('Invalid response from server')
    }

    // Clean up the stored token
    localStorage.removeItem('google_auth_invite_token')

    this.call('applyLoader', {
      value: true,
      force: true,
      timeout: 1
    })

    await this.call(
      'initializeUserSession',
      {
        loginData: loginResult
      },
      this.getRootState(),
      this.sdk
    )
    state.update({ loading: false })

    this.router('/dashboard')
  } catch (error) {
    console.error('Failed to sign in with Google:', error)
    // Clean up the stored token on error
    localStorage.removeItem('google_auth_invite_token')
    this.call('openNotification', {
      title: 'Failed to sign in',
      message: error.message,
      type: 'error'
    })
    state.update({ loading: false })
  }
}

// Check for OAuth callback on component render
export function checkGoogleOAuthCallback() {
  const state = this.getRootState()
  const urlParams = new URLSearchParams(window.location.search)
  const code = urlParams.get('code')
  const stateParam = urlParams.get('state')
  const error = urlParams.get('error')
  const storedState = sessionStorage.getItem('google_oauth_state')

  if (error) {
    console.error('Google OAuth error:', error)
    this.call('openNotification', {
      title: 'Authentication failed',
      message: 'Google authentication was cancelled or failed.',
      type: 'error'
    })
    // Clean up URL
    window.history.replaceState({}, document.title, window.location.pathname)
    return
  }

  if (code && stateParam) {
    // Verify state parameter
    if (stateParam !== storedState) {
      console.error('State parameter mismatch - possible CSRF attack')
      this.call('openNotification', {
        title: 'Security error',
        message: 'Authentication failed due to security check.',
        type: 'error'
      })
      window.history.replaceState({}, document.title, window.location.pathname)
      return
    }

    // Clean up stored state
    sessionStorage.removeItem('google_oauth_state')

    // Clean up URL first
    window.history.replaceState({}, document.title, window.location.pathname)

    // Process the OAuth code
    this.call('processGoogleOAuthCode', code, this, state)
  }
}

// Helper method to handle redirect-based Google OAuth flow
export function handleGoogleRedirectFlow() {
  try {
    // Store current URL to return to after auth
    sessionStorage.setItem('google_auth_return_url', window.location.href)

    ensurePluginSession()

    // Store invite token in localStorage instead of appending to redirect URI
    const currentUrl = new URL(window.location.href)
    const inviteToken = currentUrl.searchParams.get('token')
    if (inviteToken) {
      localStorage.setItem('google_auth_invite_token', inviteToken)
    }

    // Create the Google OAuth URL with clean redirect URI (no dynamic parameters)
    const clientId = environment.googleClientId
    const redirectUri = `${window.location.origin}/google-auth-callback`

    const encodedRedirectUri = encodeURIComponent(redirectUri)
    const scope = encodeURIComponent('openid email profile')
    const responseType = 'code'
    const oauthState = `google_oauth_${Math.random().toString(36).substr(2, 9)}`

    // Store state to verify later
    sessionStorage.setItem('google_oauth_state', oauthState)

    const googleAuthUrl =
      `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${clientId}&` +
      `redirect_uri=${encodedRedirectUri}&` +
      `response_type=${responseType}&` +
      `scope=${scope}&` +
      `state=${oauthState}&` +
      `access_type=offline&` +
      `prompt=consent`

    // Redirect to Google OAuth
    window.location.href = googleAuthUrl
  } catch (error) {
    console.error('Failed to initiate Google redirect flow:', error)
    // Clean up stored token on error
    localStorage.removeItem('google_auth_invite_token')
    this.call('openNotification', {
      title: 'Failed to sign in',
      message: 'Unable to connect to Google. Please try again.',
      type: 'error'
    })
  }
}

// Process the OAuth authorization code
export async function processGoogleOAuthCode(code) {
  const state = this.getRootState()
  try {
    state.update({ loading: true })

    // Get invite token from localStorage instead of URL
    const inviteToken = localStorage.getItem('google_auth_invite_token')

    // Use clean redirect URI (no dynamic parameters)
    const redirectUri = `${window.location.origin}/google-auth-callback`

    const pluginSession = ensurePluginSession()

    const loginResult = pluginSession
      ? await this.sdk.googleAuthCallback(code, redirectUri, inviteToken, {
          session: pluginSession
        })
      : await this.sdk.googleAuthCallback(code, redirectUri, inviteToken)

    if (!loginResult) {
      throw new Error('Invalid authentication response')
    }

    // Clean up the stored token
    localStorage.removeItem('google_auth_invite_token')

    await this.call(
      'initializeUserSession',
      {
        loginData: loginResult
      },
      this.getRootState(),
      this.sdk
    )

    state.update({ loading: false })

    this.router('/dashboard')
  } catch (error) {
    console.error('Failed to process Google OAuth code:', error)
    // Clean up stored token on error
    localStorage.removeItem('google_auth_invite_token')
    this.call('openNotification', {
      title: 'Authentication failed',
      message: error.message || 'Failed to complete Google sign-in.',
      type: 'error'
    })
    state.update({ loading: false })
  }
}
